import React, { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../store/store";
import { publicRoutes, authRoutes } from "./router.link";
import Feature from "../components/features/feature";
import AuthFeature from "../components/features/authFeature";
import { all_routes } from "./all_routes";
import ProtectedRoute from "./protectedRoute";
import { RolesEnum } from "../types/route.types";
import { PermissionType } from "../types/user.types";
import { fetchSubInfo } from "../store/slices/auth.slice";
import PricingPage from "../components/features/pricing/pricing";
import SubscriptionCallback from "../components/features/subscription/SubscriptionCallback";
import SMSCallback from "../components/features/subscription/SMSCallback";
import ContractApprovalGate from "../components/features/contracts/ContractApprovalGate";
import contractsService from "../services/contracts/contracts.service";
import { closeContractGate, openContractGate } from "../store/slices/contract.slice";

const AppRouter: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, user, sub, subLoading } = useSelector(
    (state: RootState) => state.auth
  );
  const routes = all_routes;
  const { isGateOpen } = useSelector((state: RootState) => state.contractGate);

  // Sub bilgilerini yükle
  useEffect(() => {
    if (isAuthenticated && user?.companyId && !subLoading) {
      dispatch(fetchSubInfo(user.companyId));
    }
  }, [dispatch, isAuthenticated, user?.companyId]);

  // Login sonrası /contracts/status ile gate'i erken aç/kapat.
  useEffect(() => {
    const run = async () => {
      try {
        const status = await contractsService.getStatus();
        const required = status?.requiredContracts || [];

        if (required.length > 0) {
          dispatch(
            openContractGate({
              contractType: status.contractType ?? null,
              requiredContracts: required,
              contractVersion: status.contractVersion ?? null,
              companyName: status.company?.name ?? null,
            })
          );
        } else {
          dispatch(closeContractGate());
        }
      } catch (error: any) {
        // /contracts/status 403 dönerse payload'dan gate'i manuel aç.
        const data = error?.response?.data?.data || error?.response?.data;
        const required = data?.requiredContracts || data?.requiredContractKeys || [];
        if (Array.isArray(required) && required.length > 0) {
          dispatch(
            openContractGate({
              contractType: data?.contractType ?? null,
              requiredContracts: required,
              contractVersion:
                typeof data?.contractVersion === "number" ? data.contractVersion : null,
              companyName: data?.companyName ?? data?.company?.name ?? null,
            })
          );
        }
      }
    };

    const token = localStorage.getItem("token");
    if (isAuthenticated && token) {
      run();
    }
  }, [isAuthenticated, user?.id, user?.companyId, dispatch]);

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<Navigate to={routes.login2} replace />} />
        <Route
          path="/login-2"
          element={<Navigate to={routes.login2} replace />}
        />
        <Route path="/login" element={<AuthFeature />} />
        {authRoutes.map((route, idx) => (
          <Route path={route.path} element={route.element} key={idx} />
        ))}
        <Route path="*" element={<Navigate to={routes.login2} replace />} />
      </Routes>
    );
  }

  // Sub bilgisi yüklenene kadar bekle
  if (isGateOpen) {
    return <ContractApprovalGate />;
  }

  if (isAuthenticated && user?.companyId && subLoading) {
    return <div></div>;
  }

  // ⚠️ CRITICAL: Sub durumuna göre yönlendirme kontrolü
  const isSubscriptionExpired =
    sub?.status === "expired" || sub?.status === "inactive";
  const isSuperAdmin = user?.role === RolesEnum.SUPERADMIN;

  // Expired durumda erişilebilir sayfalar
  const allowedPagesWhenExpired = [
    routes.pricing,
    routes.subscriptionCallback,
    routes.smsCallback,
    routes.paymentCallback,
  ];
  const isOnAllowedPageWhenExpired = allowedPagesWhenExpired.includes(
    window.location.pathname
  );

  // SuperAdmin'ler abonelik kontrolünden muaf
  // Eğer abonelik bittiyse ve izin verilen sayfalarda değilse ve SuperAdmin değilse, pricing'e yönlendir
  if (isSubscriptionExpired && !isSuperAdmin && !isOnAllowedPageWhenExpired) {
    return (
      <Routes>
        <Route element={<Feature />}>
          <Route
            path={routes.pricing}
            element={
              <ProtectedRoute
                element={<PricingPage />}
                allowedRoles={[PermissionType.PRICING_ACCESS]}
                userRole={[PermissionType.PRICING_ACCESS]}
                sub={sub}
                user={user}
              />
            }
          />
          <Route
            path={routes.subscriptionCallback}
            element={
              <ProtectedRoute
                element={<SubscriptionCallback />}
                allowedRoles={[
                  PermissionType.COMPANY,
                  PermissionType.PRICING_ACCESS,
                ]}
                userRole={user?.permissions as PermissionType[]}
                sub={sub}
                user={user}
              />
            }
          />
          <Route
            path={routes.smsCallback}
            element={
              <ProtectedRoute
                element={<SMSCallback />}
                allowedRoles={[
                  PermissionType.COMPANY
                ]}
                userRole={user?.permissions as PermissionType[]}
                sub={sub}
                user={user}
              />
            }
          />
        </Route>
        <Route path="*" element={<Navigate to={routes.pricing} replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Ana sayfa yönlendirmeleri */}
      {/* Rol önceliği: Admin > User/Manager > SuperAdmin */}
      {user?.role === RolesEnum.ADMIN && !isSubscriptionExpired && (
        <Route
          path="/"
          element={<Navigate to={routes.adminDashboard} replace />}
        />
      )}
      {(user?.role === RolesEnum.USER || user?.role === RolesEnum.MANAGER) &&
        !isSubscriptionExpired && (
          <Route
            path="/"
            element={<Navigate to={routes.ticketEmployee} replace />}
          />
        )}
      {user?.role === RolesEnum.SUPERADMIN && (
        <Route
          path="/"
          element={<Navigate to={routes.superAdminDashboard} replace />}
        />
      )}

      {/* Abonelik bittiyse ana sayfayı pricing'e yönlendir (SuperAdmin hariç) */}
      {isSubscriptionExpired && !isSuperAdmin && (
        <Route path="/" element={<Navigate to={routes.pricing} replace />} />
      )}

      <Route element={<Feature />}>
        {publicRoutes.map((route, idx) => (
          <Route
            path={route.path}
            key={idx}
            element={
              route.roles ? (
                <ProtectedRoute
                  element={route.element}
                  allowedRoles={route.roles as unknown as PermissionType[]}
                  userRole={user?.permissions as PermissionType[]}
                  sub={sub}
                  user={user}
                />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        ))}
      </Route>

      {/* Fallback yönlendirmeler */}
      {/* SuperAdmin her zaman dashboard'a yönlendirilir */}
      {user?.role === RolesEnum.SUPERADMIN && (
        <Route
          path="*"
          element={<Navigate to={routes.superAdminDashboard2} replace />}
        />
      )}
      {/* Diğer roller için abonelik kontrolü */}
      {user?.role === RolesEnum.ADMIN && !isSubscriptionExpired && (
        <Route
          path="*"
          element={<Navigate to={routes.adminDashboard} replace />}
        />
      )}
      {(user?.role === RolesEnum.USER || user?.role === RolesEnum.MANAGER) &&
        !isSubscriptionExpired && (
          <Route
            path="*"
            element={<Navigate to={routes.ticketEmployee} replace />}
          />
        )}

      {/* Abonelik bittiyse tüm bilinmeyen rotaları pricing'e yönlendir (SuperAdmin hariç) */}
      {isSubscriptionExpired && !isSuperAdmin && (
        <Route path="*" element={<Navigate to={routes.pricing} replace />} />
      )}
    </Routes>
  );
};

export default AppRouter;
